import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Building2, Target, CheckCircle, Loader2 } from "lucide-react";
import { useMutation, useApolloClient } from "@apollo/client/react";
import { COMPLETE_ORGANIZATION_ONBOARDING, COMPLETE_EMAIL_ONBOARDING } from "@/graphql/mutations";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { saveTokens } from "@/lib/token-utils";
import { getStoredDashboardAttribution } from "@/lib/analytics";

interface OrganizationOnboardingProps {
  step: number;
  onNext: () => void;
  onBack: () => void;
  onComplete: (data: OrgData) => void;
  apolloClient: ReturnType<typeof useApolloClient>;
}

interface OrgData {
  name: string;
  size: string;
  industry: string;
  customIndustry: string;
  motivation: string;
  expectedSessions: string;
  useCases: string;
  hearAboutUs: string;
  customHearAboutUs: string;
}

const OrganizationOnboarding = ({ step, onNext, onBack, onComplete, apolloClient }: OrganizationOnboardingProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orgData, setOrgData] = useState<OrgData>({
    name: "",
    size: "",
    industry: "",
    customIndustry: "",
    motivation: "",
    expectedSessions: "",
    useCases: "",
    hearAboutUs: "",
    customHearAboutUs: "",
  });

  const handleOnboardingSuccess = async (result: { success: boolean; error?: string; tokens?: { accessToken: string; refreshToken: string } }) => {
    if (!result.success) {
      toast({
        title: "Error",
        description: result.error || "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (result.tokens) {
      saveTokens(result.tokens.accessToken, result.tokens.refreshToken);
      await apolloClient.resetStore();
    }

    // TODO: Send invitations if any were added
    // For now, we'll skip this and handle it later

    sessionStorage.removeItem('pendingSignupSource');
    // Redirect to dashboard
    window.location.href = "/";
  };

  const handleOnboardingError = (error: Error) => {
    console.error("Onboarding error:", error);
    toast({
      title: "Error",
      description: error.message || "Failed to complete onboarding. Please try again.",
      variant: "destructive",
    });
  };

  const [completeOnboarding, { loading: isCompletingGoogle }] = useMutation(COMPLETE_ORGANIZATION_ONBOARDING, {
    onCompleted: (data: { completeOrganizationOnboarding: { success: boolean; error?: string; tokens?: { accessToken: string; refreshToken: string } } }) => handleOnboardingSuccess(data.completeOrganizationOnboarding),
    onError: handleOnboardingError,
  });

  const [completeEmailOnboarding, { loading: isCompletingEmail }] = useMutation(COMPLETE_EMAIL_ONBOARDING, {
    onCompleted: (data: { completeEmailOnboarding: { success: boolean; error?: string; tokens?: { accessToken: string; refreshToken: string } } }) => handleOnboardingSuccess(data.completeEmailOnboarding),
    onError: handleOnboardingError,
  });

  const isCompleting = isCompletingGoogle || isCompletingEmail;

  const isStepValid = () => {
    switch (step) {
      case 1:
        if (!orgData.name || !orgData.size) return false;
        if (orgData.industry === "other") {
          return !!orgData.customIndustry.trim();
        }
        return !!orgData.industry;
      case 2:
        if (!orgData.motivation || !orgData.expectedSessions) return false;
        if (!orgData.hearAboutUs) return false;
        if (orgData.hearAboutUs === "other" && !orgData.customHearAboutUs.trim()) return false;
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (step === 2) {
      // Last step - complete onboarding
      if (!orgData.name) {
        toast({
          title: "Error",
          description: "Organization name is required",
          variant: "destructive",
        });
        return;
      }

      const googleIdToken = sessionStorage.getItem('pendingGoogleIdToken');
      const pendingEmail = sessionStorage.getItem('pendingEmailSignup');

      if (!googleIdToken && !pendingEmail) {
        toast({
          title: "Error",
          description: "Authentication not found. Redirecting to sign in...",
          variant: "destructive",
        });
        setTimeout(() => {
          navigate("/login");
        }, 1500);
        return;
      }

      // Prepare form data
      const formData = {
        name: orgData.name,
        size: orgData.size,
        industry: orgData.industry === "other" 
          ? orgData.customIndustry 
          : orgData.industry,
        motivation: orgData.motivation,
        expectedSessions: orgData.expectedSessions,
        useCases: orgData.useCases,
        hearAboutUs: (orgData.hearAboutUs === "other"
          ? orgData.customHearAboutUs.trim()
          : orgData.hearAboutUs),
        attribution: getStoredDashboardAttribution(),
      };

      try {
        if (googleIdToken) {
          // Google SSO user
          await completeOnboarding({
            variables: {
              input: {
                organizationName: orgData.name,
                formData: formData,
                googleIdToken: googleIdToken,
              },
            },
          });
          sessionStorage.removeItem('pendingGoogleIdToken');
        } else if (pendingEmail) {
          // Email signup user
          await completeEmailOnboarding({
            variables: {
              input: {
                email: pendingEmail,
                organizationName: orgData.name,
                formData: formData,
              },
            },
          });
          sessionStorage.removeItem('pendingEmailSignup');
        }
      } catch (error) {
        // Error handled by onError callback
      }
    } else {
      onNext();
    }
  };

  return (
    <>
      {/* Step 1: Organization Details */}
      {step === 1 && (
        <Card className="p-8 border-border shadow-xl animate-fade-in">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Organization Details</h2>
                <p className="text-muted-foreground">Tell us about your organization</p>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name *</Label>
                <Input
                  id="org-name"
                  placeholder="FlowLabs Inc."
                  value={orgData.name}
                  onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                  className="h-12"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-size">Organization Size *</Label>
                  <Select
                    value={orgData.size}
                    onValueChange={(value) => setOrgData({ ...orgData, size: value })}
                  >
                    <SelectTrigger id="org-size" className="h-12">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="500+">500+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Select
                    value={orgData.industry}
                    onValueChange={(value) =>
                      setOrgData({ ...orgData, industry: value, customIndustry: value === "other" ? orgData.customIndustry : "" })
                    }
                  >
                    <SelectTrigger id="industry" className="h-12">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="media">Media & Entertainment</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="consulting">Consulting</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {orgData.industry === "other" && (
                    <div className="mt-2">
                      <Input
                        id="custom-industry"
                        placeholder="Please specify your industry"
                        value={orgData.customIndustry}
                        onChange={(e) =>
                          setOrgData({ ...orgData, customIndustry: e.target.value })
                        }
                        className="h-12"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button variant="outline" onClick={onBack} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <Button onClick={onNext} disabled={!isStepValid()} className="gap-2">
                Continue
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: Motivation & Usage */}
      {step === 2 && (
        <Card className="p-8 border-border shadow-xl animate-fade-in">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Usage & Goals</h2>
                <p className="text-muted-foreground">Help us understand your needs</p>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="motivation">What brings you to FlowLabs? *</Label>
                <Textarea
                  id="motivation"
                  placeholder="e.g., We need a reliable streaming solution for our remote team meetings..."
                  value={orgData.motivation}
                  onChange={(e) => setOrgData({ ...orgData, motivation: e.target.value })}
                  className="min-h-[100px] resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sessions">Expected Monthly Sessions *</Label>
                <Select
                  value={orgData.expectedSessions}
                  onValueChange={(value) => setOrgData({ ...orgData, expectedSessions: value })}
                >
                  <SelectTrigger id="sessions" className="h-12">
                    <SelectValue placeholder="Select expected usage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 sessions</SelectItem>
                    <SelectItem value="11-25">11-25 sessions</SelectItem>
                    <SelectItem value="26-50">26-50 sessions</SelectItem>
                    <SelectItem value="51-100">51-100 sessions</SelectItem>
                    <SelectItem value="100+">100+ sessions</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hear-about-us">How did you hear about Flow? *</Label>
                <Select
                  value={orgData.hearAboutUs}
                  onValueChange={(value) =>
                    setOrgData({ ...orgData, hearAboutUs: value, customHearAboutUs: value === "other" ? orgData.customHearAboutUs : "" })
                  }
                >
                  <SelectTrigger id="hear-about-us" className="h-12">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="search">Search engine (Google, Bing...)</SelectItem>
                    <SelectItem value="reddit">Reddit</SelectItem>
                    <SelectItem value="forum">Forum (Lift Gamma Gain, Blackmagic...)</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="social">Social media</SelectItem>
                    <SelectItem value="word-of-mouth">Friend or colleague</SelectItem>
                    <SelectItem value="email">Email from FlowLabs</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {orgData.hearAboutUs === "other" && (
                  <div className="mt-2">
                    <Input
                      id="custom-hear-about-us"
                      placeholder="Please tell us where"
                      maxLength={200}
                      required
                      value={orgData.customHearAboutUs}
                      onChange={(e) =>
                        setOrgData({ ...orgData, customHearAboutUs: e.target.value })
                      }
                      className="h-12"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="use-cases">Primary Use Cases (Optional)</Label>
                <Textarea
                  id="use-cases"
                  placeholder="e.g., Team meetings, webinars, client presentations..."
                  value={orgData.useCases}
                  onChange={(e) => setOrgData({ ...orgData, useCases: e.target.value })}
                  className="min-h-[80px] resize-none"
                />
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button variant="outline" onClick={onBack} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <Button 
                onClick={handleNext} 
                disabled={!isStepValid() || isCompleting} 
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Complete Setup
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default OrganizationOnboarding;
